import type { HTMLAttributes, ReactNode } from 'react';

import {
  CheckboxGroupContext,
  type CheckboxGroupContextValue,
} from './CheckboxGroupContext';

export interface CheckboxGroupProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** The entries currently ticked, in the order they were ticked. */
  value: string[];
  /** Receives the next array: a tick appends its `value`, an untick removes it. */
  onChange: (value: string[]) => void;
  /** Disables every box in the group, with or without a `value`. */
  disabled?: boolean;
  children: ReactNode;
}

/**
 * The next group value after one entry is ticked or unticked: a tick appends
 * it (so the array keeps ticking order), an untick removes it.
 */
export const toggleGroupValue = <T extends string>(
  selected: readonly T[],
  value: T,
  checked: boolean
): T[] =>
  checked ? [...selected, value] : selected.filter((entry) => entry !== value);

/**
 * Groups [[Checkbox]] children (each keyed by `value`) into one selection
 * array. Controlled only: the parent owns `value`.
 */
export const CheckboxGroup = ({
  value,
  onChange,
  disabled = false,
  children,
  ...rest
}: CheckboxGroupProps) => {
  const contextValue: CheckboxGroupContextValue = { value, onChange, disabled };

  return (
    <CheckboxGroupContext.Provider value={contextValue}>
      <div role="group" {...rest}>
        {children}
      </div>
    </CheckboxGroupContext.Provider>
  );
};
